import React, { useEffect, useRef, useState } from 'react'
import Icon from './icons'

// Ported from the source project's src/components/site/Addresses.tsx - the slider's state
// logic (current index, mobile detection via matchMedia, swipe handlers) is copied
// unchanged rather than reimplemented, per the task's own instruction. TypeScript types
// dropped, otherwise identical: one card per view on mobile (swipeable, dot nav reflects
// the active card), all cards visible in a row on desktop.
//
// `phone`/`email` are new on each address (added to src/content/site.js from the live
// labs/index.jsx) and have no dedicated class in site.css's ADDRESSES SLIDER block - this
// task is not authorised to add CSS here, so they render as two extra lines inside the
// existing `.addr-text` block, as tel:/mailto: links per the task's own linking rule.
const Addresses = ({ section, addresses }) => {
    const [current, setCurrent] = useState(0);
    const [isMobile, setIsMobile] = useState(false);
    const startX = useRef(null);
    const total = addresses.length;

    useEffect(() => {
        const mq = window.matchMedia('(max-width: 768px)');
        const update = () => setIsMobile(mq.matches);
        update();
        mq.addEventListener('change', update);
        return () => mq.removeEventListener('change', update);
    }, []);

    const go = (idx) => setCurrent(((idx % total) + total) % total);
    const move = (dir) => go(current + dir);

    const transform = isMobile ? `translateX(calc(-${current * 100}% - ${current * 24}px))` : 'translateX(0)';

    const onTouchStart = (e) => {
        startX.current = e.touches[0].clientX;
    };
    const onTouchEnd = (e) => {
        if (startX.current === null) return;
        const dx = e.changedTouches[0].clientX - startX.current;
        if (Math.abs(dx) > 40) move(dx < 0 ? 1 : -1);
        startX.current = null;
    };

    return (
        <section className="addresses-section">
            <div className="container">
                <div className="section-eyebrow">{section.eyebrow}</div>
                <h2 className="section-title">{section.title}</h2>
                <div className="addr-track-wrap" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
                    <div className="addr-track" style={{ transform }}>
                        {addresses.map((a) => (
                            <div className="addr-card" key={a.order}>
                                <div className="addr-icon">
                                    <Icon name="pin" />
                                </div>
                                <div className={`addr-badge${a.isHq ? ' hq' : ''}`}>{a.badge}</div>
                                <div className="addr-name">{a.name}</div>
                                <div className="addr-text">
                                    {a.lines.map((line, i) => (
                                        <span key={i}>
                                            {line}
                                            {i < a.lines.length - 1 && <br />}
                                        </span>
                                    ))}
                                    <br />
                                    <a href={`tel:${a.phone}`}>{a.phone}</a>
                                    <br />
                                    <a href={`mailto:${a.email}`}>{a.email}</a>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="addr-nav">
                    <button className="addr-prev" aria-label="Previous" onClick={() => move(-1)}>←</button>
                    {addresses.map((a, i) => (
                        <button
                            key={a.order}
                            className={`addr-dot${i === current ? ' active' : ''}`}
                            aria-label={`Go to ${a.name}`}
                            onClick={() => go(i)}
                        />
                    ))}
                    <button className="addr-next" aria-label="Next" onClick={() => move(1)}>→</button>
                </div>
            </div>
        </section>
    );
};

export default Addresses;
